import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { DateValue } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { classNames } from "@/utils/Classnames";
import { useGetDrivers, postScheduleCollection } from "@/services/LogisticsCoordinator";
import { FullContainerOverview } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Text from "@/components/atoms/Text";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Avatar, SectionTitle, initialsOf } from "../shared";
import styles from "./styles.module.css";
import shared from "../styles.module.css";

// After scheduling succeeds, keep the confirmation on screen briefly before the
// full-containers list refetches (which drops this container and closes the panel).
const REFRESH_DELAY_MS = 3000;

/**
 * Collections detail panel. Pick a date, then a driver — selecting a driver creates the
 * scheduled collection in a single call (date + driver). Any later change is done from the
 * Scheduled view. Mounted with `key={containerId}` by the parent, so it resets per container.
 */
const CollectionDetail: FC<{ container: FullContainerOverview }> = ({ container }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [chosenDate, setChosenDate] = useState<DateValue | undefined>(() => new Date());
  const [scheduledDriver, setScheduledDriver] = useState<string | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(refreshTimer.current), []);

  const plannedDateISO = chosenDate ? dayjs(chosenDate).format("YYYY-MM-DD") : "";
  const scheduled = scheduledDriver !== null;

  const { data: driverData } = useGetDrivers(plannedDateISO, { enabled: !!plannedDateISO && !scheduled });
  const drivers = driverData?.drivers ?? [];
  const availCount = driverData?.availableCount ?? 0;
  const fleetSize = driverData?.fleetSize ?? 0;

  // Picking a driver creates the scheduled collection (date + driver in one call).
  const scheduleCollection = useMutation({
    mutationFn: (driverUserId: number) =>
      postScheduleCollection(container.containerId, dayjs(chosenDate).toISOString(), driverUserId),
    onSuccess: (_res, driverUserId) => {
      const name = drivers.find((d) => d.userId === driverUserId)?.name ?? "";
      setScheduledDriver(name);
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("containers:toast.driverAssigned", { name }))
      );
      // Delay the list refresh so the confirmation stays visible for a moment.
      refreshTimer.current = window.setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ["getFullContainers"] }),
        REFRESH_DELAY_MS
      );
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.assignError")));
    },
  });

  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
        <div className={shared.detailHeaderText}>
          <Text size="sm">
            {container.wasteTypeName} · {container.siteName}
          </Text>
          <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={6}>
            {t("containers:detail.reported", {
              date: dayjs(container.reported).format("DD MMM"),
              abs: dayjs(container.reported).format("HH:mm"),
            })}
          </Text>
        </div>
      </div>

      <div className={shared.detailBody}>
        <SectionTitle>{t("containers:detail.scheduleCollection")}</SectionTitle>

        <div className={shared.editPanel} style={{ marginTop: 14 }}>
          <DatePickerInput
            label={t("containers:detail.pickDate")}
            placeholder={t("actions:selectDate")}
            value={chosenDate}
            onChange={setChosenDate}
            disabled={scheduled || scheduleCollection.isPending}
          />
        </div>

        <div className={shared.divider}>
          <div className={shared.detailSectionHeader}>
            <SectionTitle>{t("containers:detail.assignDriver")}</SectionTitle>
            {!scheduled && (
              <Text span display="technical" size="xs" tt="uppercase" color="secondary">
                {t("containers:detail.fleetMeta", { available: availCount, total: fleetSize })}
              </Text>
            )}
          </div>

          {!scheduled && (
            <div className={shared.driverList} style={{ marginTop: 12 }}>
              {drivers.map((d) => (
                <button
                  key={d.userId}
                  type="button"
                  disabled={!d.available || scheduleCollection.isPending}
                  onClick={d.available ? () => scheduleCollection.mutate(d.userId) : undefined}
                  className={classNames(shared.driverRow, d.available ? "" : shared.driverRowDisabled)}
                >
                  <Avatar initials={initialsOf(d.name)} size={34} variant={d.available ? "active" : "disabled"} />
                  <span className={shared.driverMeta}>
                    <span className={shared.driverName}>
                      <Text span size="sm" strong color={d.available ? "primary" : "secondary"}>
                        {d.name}
                      </Text>
                    </span>
                  </span>
                  <Text span display="technical" size="xs" tt="uppercase" color={d.available ? "success" : "secondary"}>
                    {d.available ? t("containers:driver.available") : t("containers:driver.full")}
                  </Text>
                </button>
              ))}
            </div>
          )}

          {scheduled && (
            <div className={styles.assignedCard} style={{ marginTop: 12 }}>
              <div className={styles.assignedTop}>
                <Avatar initials={initialsOf(scheduledDriver ?? "")} size={38} />
                <div style={{ flex: 1 }}>
                  <Text size="md" strong>
                    {scheduledDriver}
                  </Text>
                </div>
              </div>
              <div className={styles.assignedFooter}>
                <SVGAsset name="checkmark-green" width={15} height={15} />
                <Text size="sm" color="success">
                  {t("containers:detail.collectionScheduled")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default CollectionDetail;
